import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, type BillingMe } from "@/lib/api";
import { DOLLAR_IMG } from "@/lib/dollar";

const STEPS = [
  { to: "/presupuesto", title: "Arma tu presupuesto", text: "Tus ingresos y en qué quieres gastarlos." },
  { to: "/cuentas", title: "Agrega tus cuentas", text: "Débito, efectivo, ahorro, tarjetas y créditos." },
  { to: "/movimientos", title: "Registra tus movimientos", text: "Así aprendo contigo y te aviso a tiempo." },
];

function NameStep({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Escribe tu nombre");
    setBusy(true);
    try { await api.patch("/settings/profile", { name: name.trim() }); onDone(); }
    catch (err) { toast.error(formatApiError(err)); setBusy(false); }
  };
  return (
    <form onSubmit={save} className="mx-auto mt-6 flex max-w-sm gap-2" data-testid="welcome-name-form">
      <Input value={name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)} placeholder="¿Cómo te llamas?" maxLength={60} className="h-11 bg-white" data-testid="welcome-name-input" />
      <button type="submit" disabled={busy} className="h-11 rounded-full bg-[#1B2A4A] px-5 font-semibold text-white disabled:opacity-60" data-testid="welcome-name-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Listo"}</button>
    </form>
  );
}

export default function WelcomePage() {
  const { user, refresh } = useAuth();
  const [done, setDone] = useState(false);
  const [tries, setTries] = useState(0);
  useEffect(() => {
    if (done) return;
    const t = setTimeout(async () => {
      const r = await api.get<BillingMe>("/billing/me").catch(() => null);
      if (r && ["trialing", "active", "canceling", "exempt"].includes(r.data.status)) { setDone(true); await refresh(); } else setTries((n) => n + 1);
    }, tries === 0 ? 300 : 2500);
    return () => clearTimeout(t);
  }, [tries, done, refresh]);

  if (!done) {
    return (
      <section className="fade-up mx-auto max-w-lg py-16 text-center" data-testid="welcome-confirming">
        <Loader2 className="mx-auto h-10 w-10 animate-spin text-[#2C4F91]" />
        <h1 className="mt-4 font-display text-2xl font-bold text-[#1B2A4A]">Estamos confirmando tu pago</h1>
        <p className="mt-2 text-slate-600">Esto tarda unos segundos. La página se actualiza sola.</p>
        {tries > 20 && <p className="mt-4 text-sm text-slate-500">Está tardando más de lo normal. Puedes cerrar esta página; te avisaremos por correo.</p>}
      </section>
    );
  }
  const askName = !!(user && user.setup_pending);
  return (
    <section className="fade-up mx-auto max-w-2xl py-10 text-center" data-testid="welcome-page">
      <img src={DOLLAR_IMG.puppy.full} alt="Dollar" className="mx-auto h-48 w-auto" />
      <h1 className="mt-4 font-display text-3xl font-bold text-[#1B2A4A]" data-testid="welcome-title">¡Bienvenido a Otium!</h1>
      <p className="mx-auto mt-3 max-w-md text-[#1A1F2B]">Soy Dollar, tu mentor. Tu suscripción ya está activa. {askName ? "Primero, ¿cómo te llamo?" : "Vamos a configurar tus números juntos."}</p>
      {askName ? <NameStep onDone={refresh} /> : (
        <div className="mt-8 grid gap-3 text-left sm:grid-cols-3" data-testid="welcome-steps">
          {STEPS.map((s, i) => (
            <Link key={s.to} to={s.to} className="rounded-2xl border border-[#E7DECF] bg-white p-5 transition-transform hover:-translate-y-0.5" data-testid={`welcome-step-${i + 1}`}>
              <p className="text-xs font-semibold tracking-widest text-[#7A6548]">PASO {i + 1}</p>
              <p className="mt-1 font-semibold text-[#1B2A4A]">{s.title}</p>
              <p className="mt-1 text-sm text-slate-500">{s.text}</p>
            </Link>
          ))}
        </div>
      )}
      {!askName && <Link to="/resumen" className="mt-6 inline-block text-sm font-semibold text-[#2C4F91] underline" data-testid="welcome-skip">Ir a mi resumen</Link>}
    </section>
  );
}
